import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';

const SkeletonBox = ({ className = '' }) => (
  <div className={`rounded animate-pulse ${className}`} style={{ backgroundColor: '#1f1f1f' }} />
);

const formatShort = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000)?.toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000)?.toFixed(0)}K`;
  return Math.round(n)?.toString();
};

const CustomTooltip = ({ active, payload, serverColor, t }) => {
  if (!active || !payload?.length) return null;
  const d = payload?.[0]?.payload;
  return (
    <div
      className="rounded-lg p-3 border text-xs"
      style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
    >
      <p className="font-semibold text-white mb-1">{d?.name}</p>
      <p style={{ color: serverColor }}>💎 {formatShort(d?.diamonds)} diamonds</p>
      <p style={{ color: 'rgba(255,255,255,0.5)' }}>👥 {d?.active_creators} {t('admin.performanceActuelle.activeCreatorsTooltip')}</p>
    </div>
  );
};

const CreatorLabel = ({ x, y, width, height, value, t }) => {
  if (!value) return null;
  return (
    <text
      x={x + width + 6}
      y={y + height / 2}
      dy={4}
      fill="rgba(255,255,255,0.4)"
      fontSize={10}
      fontFamily="Inter, sans-serif"
    >
      {value} {t('admin.performanceActuelle.creatorsLabel')}
    </text>
  );
};

const ManagerComparisonChart = ({ managerPerfData, serverColor, loading }) => {
  const { t } = useTranslation();
  const chartData = useMemo(() => {
    return (managerPerfData || [])?.map((m) => ({
      name: m?.name,
      diamonds: m?.diamonds,
      active_creators: m?.active_creators,
    }));
  }, [managerPerfData]);

  if (loading) {
    return (
      <div className="rounded-xl p-5 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
        <SkeletonBox className="h-48 w-full" />
      </div>
    );
  }

  if (!chartData?.length) {
    return (
      <div
        className="rounded-xl p-5 border flex items-center justify-center h-32"
        style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}
      >
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performanceActuelle.noDataAvailable')}
        </p>
      </div>
    );
  }

  const chartHeight = Math.max(200, chartData?.length * 48);

  return (
    <div className="rounded-xl p-5 border" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 120, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
            tickFormatter={formatShort}
            axisLine={{ stroke: '#2a2a2a' }}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11, fontFamily: 'Inter, sans-serif' }}
            axisLine={{ stroke: '#2a2a2a' }}
            tickLine={false}
            width={140}
          />
          <Tooltip content={<CustomTooltip serverColor={serverColor} t={t} />} />
          <Bar dataKey="diamonds" radius={[0, 4, 4, 0]}>
            <LabelList dataKey="active_creators" content={<CreatorLabel t={t} />} />
            {chartData?.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={serverColor} fillOpacity={1 - index * 0.06} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ManagerComparisonChart;
